import { AnalyticsService, AnalyticsGranularity } from './../../../services/analytics.service';
import { PublicationService } from './../../../services/publication.service';
import { ProjectKpiService, KPICalculation } from './../../../services/project-kpi.service';
import { DateRange } from './../../../interfaces/dateRange';
import { ProjectService } from './../../../services/project.service';
import { SapiService } from './../../../services/sapi.service';
import { ProjectStatsConfiguration, Project } from './../../../interfaces/project';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { OverlayService } from './../../../services/overlay.service';
import { Publication } from './../../../interfaces/publication';

import dayjs from 'dayjs';
import { ChartDataset, ScriptableContext } from 'chart.js';
import { BbPluginOptions } from 'app/components/shared/bb-chart/bb-chart.component';

const KPI_DATEFORMAT_OPTIONS: any = { month: 'short' };

export const GREEN = '#8EDA3F';
const GREEN_HOVER = '#8EDA3F';
const GREEN_TRANSPARENT = '#fff';

export const ORANGE = '#E78149';
const ORANGE_HOVER = '#E78149';
const ORANGE_TRANSPARENT = '#fff';

export const BLUE = '#9EC1E6';
const BLUE_HOVER = '#9EC1E6';
const BLUE_TRANSPARENT = '#fff';

export interface StatsDataItem {
	x: string;
	y: number;
	kpiGoalReached?: boolean;
	hasKpiGoal?: boolean;
	beginMissing?: boolean;
	endMissing?: boolean;
}

@Component({
	selector: 'project-stats-performance',
	templateUrl: './project-stats-performance.component.html',
	styleUrls: ['./project-stats-performance.component.scss']
})

export class ProjectStatsPerformanceComponent implements OnInit, OnDestroy {
	@Input() project: Project;
	@Input() configuration: ProjectStatsConfiguration;

	previousPerformanceParams: any;
	performanceStatsData: any;
	goalLine: any;
	loadingViewData = false;
	loadingPerformanceData = false;
	numPoints: number;
	viewers: number;
	totalViewtime: number;
	haveGraphData = false;
	havePerformanceData = false;
	granularity: string;
	selectedKPI: KPICalculation;
	timeZoneName = 'Europe/Berlin';
	viewersGraphDatasets: ChartDataset[];
	kpiGraphDatasets: ChartDataset[];
	pluginOptions: BbPluginOptions;

	statsDataItems: StatsDataItem[];
	performanceDataYMax: number;

	xAxisLabels: string[];
	tooltipFirstItem: string;
	tooltipLastItem: string;

	private monthStartsAtBegin: boolean;
	private monthStopsAtEnd: boolean;
	private subscriptions = new Subscription();

	private lastPerformanceDateRange: DateRange;
	private lastContext: string;

	constructor(
		private projectKpiService: ProjectKpiService,
		private sapiService: SapiService,
		private projectService: ProjectService,
		private publicationService: PublicationService,
		private analyticsService: AnalyticsService,
		private overlayService: OverlayService
	) {
		this.subscriptions.add(this.publicationService.getData().subscribe((pub: Publication) => {
			if (pub && pub.timeZone) {
				this.timeZoneName = pub.timeZone;
			}
		}));

		this.pluginOptions = {
			bbBarLabel: {
				display: true,
				toFixed: 0
			},
			bbBarGoalLine: {
				display: true
			}
		};
	}

	ngOnInit() {
		this.subscriptions.add(this.projectService.statsDateRangeSubject.subscribe((dateRange: DateRange) => {
			const context = this.projectService.projectContext;

			if (this.lastPerformanceDateRange?.startDate === dateRange.startDate && this.lastPerformanceDateRange?.endDate === dateRange.endDate && context === this.lastContext) {
				return;
			}

			this.dateRangeChanged();

			this.lastPerformanceDateRange = dateRange;
			this.lastContext = context;
		}));

		this.subscriptions.add(this.projectKpiService.kpiSelectionSubject.subscribe((selectedKPI: KPICalculation) => {
			this.onKpiSelected(selectedKPI);
		}));

		if (this.project?.kpiCalculations?.length > 0) {
			this.projectKpiService.selectKPI(this.project.kpiCalculations[0]);
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	updateDataItemGoals() {
		const kpiGoal = this.selectedKPI ? this.selectedKPI.goal : false;
		let lastStatsDataItem = null;

		if (this.statsDataItems?.length > 0) {
			if (this.statsDataItems.length > 1) {
				lastStatsDataItem = this.statsDataItems[this.statsDataItems.length - 1];
			}

			this.statsDataItems.forEach((statsDataItem: StatsDataItem) => {
				statsDataItem.hasKpiGoal = !!this.selectedKPI.goal;
				statsDataItem.kpiGoalReached = true;
				if (statsDataItem.y > -1 && kpiGoal && statsDataItem.y <= kpiGoal) {
					statsDataItem.kpiGoalReached = false;
				}
			});

			if (!this.monthStartsAtBegin) {
				this.statsDataItems[0].beginMissing = true;
			}

			if (!this.monthStopsAtEnd && lastStatsDataItem) {
				lastStatsDataItem.endMissing = true;
			}

			if (this.kpiGraphDatasets && this.kpiGraphDatasets.length) {
				this.kpiGraphDatasets[0].data = this.statsDataItems as any;
			}

			this.kpiGraphDatasets = [...this.kpiGraphDatasets];
		}
	}

	dateRangeChanged() {
		const timeDiff = Math.abs(this.configuration.statsDateEnd.getTime() - this.configuration.statsDateStart.getTime());
		const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

		this.monthStartsAtBegin = this.configuration.statsDateStart.getDate() === 1;
		this.monthStopsAtEnd = this.configuration.statsDateEnd.getDate().toString() === dayjs(this.configuration.statsDateEnd).endOf('month').format('D');

		this.granularity = diffDays >= 14 ? 'DAY' : 'HOUR';
		this.getViewerStatsData(this.getParams(this.configuration));
		if (this.selectedKPI) {
			this.kpiGetStats(this.selectedKPI, true, true);
		}

		this.setTooltipsIncompleteMonths();
		this.updateDataItemGoals();
	}

	setTooltipsIncompleteMonths(): void {
		if (this.configuration.statsDateStart.getDate() !== 1) {
			this.tooltipFirstItem = `(${this.configuration.statsDateStart.getDate().toString()}-${dayjs(this.configuration.statsDateStart).endOf('month').format('D')})`;
		} else {
			this.tooltipFirstItem = null;
		}

		if (!this.monthStopsAtEnd) {
			this.tooltipLastItem = `(${dayjs(this.configuration.statsDateEnd).startOf('month').format('D')}-${this.configuration.statsDateEnd.getDate().toString()})`;
		} else {
			this.tooltipLastItem = null;
		}
	}

	onKpiSelected(selectedKPI: KPICalculation) {
		const kpiIds = this.project?.kpiCalculations?.map((k: KPICalculation) => k.id);
		const updateKpi = !this.selectedKPI || !this.selectedKPI.id || this.selectedKPI.id !== selectedKPI.id;

		this.selectedKPI = selectedKPI;

		if (this.selectedKPI?.goal) {
			// merge object to trigger ngOnChanges in bb-chart
			this.pluginOptions = {
				...this.pluginOptions, ...{
					bbBarGoalLine:
					{
						display: true,
						goal: this.selectedKPI.goal
					}
				}
			};
		} else {
			this.pluginOptions = {
				...this.pluginOptions, ...{
					bbBarGoalLine:
					{
						display: false,
						goal: null
					}
				}
			};
		}

		if (kpiIds && kpiIds.length > 0 && kpiIds.indexOf(selectedKPI.id) !== -1 && updateKpi) {
			this.kpiGetStats(selectedKPI);
		}
	}

	getBackgroundColors(scriptableContext: ScriptableContext<'bar'>): any {
		const chartArea = scriptableContext.chart.chartArea;
		const context = scriptableContext.chart.ctx;
		const data = scriptableContext.dataset.data;

		if (!chartArea) {
			return null;
		}

		const chartWidth = chartArea.right - chartArea.left;

		const hover = scriptableContext.active;
		const green = hover ? GREEN_HOVER : GREEN;
		const orange = hover ? ORANGE_HOVER : ORANGE;
		const blue = hover ? BLUE_HOVER : BLUE;

		const colors = [];

		data.forEach((element: any, index: number) => {
			if (element.beginMissing || element.endMissing) {
				const elementSize = (chartWidth / data.length);
				const elementPosition = elementSize * index + chartArea.left;
				const gradient = context.createLinearGradient(elementPosition, 0, elementPosition + elementSize, 0);

				const firstStop = element.beginMissing ? 0.9 : 0;
				const secondStop = element.beginMissing ? 0 : 0.9;

				if (!element.hasKpiGoal) {
					gradient.addColorStop(firstStop, blue);
					gradient.addColorStop(secondStop, BLUE_TRANSPARENT);
				} else if (element.kpiGoalReached) {
					gradient.addColorStop(firstStop, green);
					gradient.addColorStop(secondStop, GREEN_TRANSPARENT);
				} else {
					gradient.addColorStop(firstStop, orange);
					gradient.addColorStop(secondStop, ORANGE_TRANSPARENT);
				}

				colors.push(gradient);
			} else {
				if (!element.hasKpiGoal) {
					colors.push(blue);
				} else if (element.kpiGoalReached) {
					colors.push(green);
				} else {
					colors.push(orange);
				}
			}
		});

		return colors;
	}

	handlePerformanceStatsData(kpi: KPICalculation) {
		if (kpi) {
			this.havePerformanceData = true;
			this.kpiGraphDatasets = [];

			this.updateDataItemGoals();

			const max = Math.max(...this.statsDataItems.map(data => data.y));
			this.performanceDataYMax = (kpi.goal ? Math.max(kpi.goal, max) : max) + 3;

			const kpiGraphDataset: ChartDataset<'bar'> = {
				label: kpi.label,
				data: this.statsDataItems as any,
				borderWidth: -1,
				borderColor: ['#3693ef'],
				backgroundColor: this.getBackgroundColors,
				hoverBackgroundColor: this.getBackgroundColors
			};

			this.kpiGraphDatasets.push(kpiGraphDataset);
		}
	}

	kpiGetStats(kpi: KPICalculation, getTotals?: boolean, force = false) {
		if (!this.configuration?.statsDateStart) {
			return;
		}

		// note: performance API call will return all KPI metrics for this project
		if (this.statsDataItems?.length > 0 && !force) {
			this.statsDataItems = this.processPerformanceData(this.performanceStatsData, kpi);
			this.handlePerformanceStatsData(kpi);
			return;
		}

		const performanceParams = this.getPerformanceParams(this.configuration, kpi, getTotals);
		const loadId = 'ProjectStatsPerformanceComponent:getPerformanceStatsData';
		this.overlayService.increaseLoadCount(loadId);
		this.loadingPerformanceData = true;
		this.projectService.getProjectPerformanceStatsData(performanceParams).subscribe((result: any) => {
			this.performanceStatsData = result;
			this.loadingPerformanceData = false;

			this.statsDataItems = this.processPerformanceData(result, kpi);
			this.overlayService.decreaseLoadCount(loadId);
			this.handlePerformanceStatsData(kpi);
		});
	}

	dateToStartOfMonth(date: Date) {
		const startOfMonthDate = new Date(date);
		startOfMonthDate.setDate(1);
		startOfMonthDate.setHours(0, 0, 0, 0);
		return startOfMonthDate;

	}

	getPerformanceParams(configuration: ProjectStatsConfiguration, kpi?: KPICalculation, getTotals?: boolean) {
		const params: any = {
			TZ: this.timeZoneName,
			granularity: 'month',
			fromDate: configuration.statsDateStart.toISOString(),
			toDate: configuration.statsDateEnd.toISOString(),
		};
		if (this.projectService.projectContext) {
			params.context = this.projectService.projectContext;
		} else if (params.context) {
			delete params.context;
		}
		// need to get all KPI's for now for the totals
		// if (!getTotals && kpi && kpi.id) {
		// 	params.kpi = JSON.stringify([kpi.id]);
		// }
		return params;
	}

	getParams(configuration: ProjectStatsConfiguration) {
		const params = {
			rows: 0,
			fromDate: configuration.statsDateStart.toISOString(),
			toDate: configuration.statsDateEnd.toISOString(),
			TZ: this.timeZoneName,
			// q: 'mainContentStarted_bool:'true'',
			// 'fq': `projectId_str:${ this.project.id }`,
			fq: [
				`projectId_str:${this.project.id}`,
				'mainContentStarted_bool:"true"'
			],
			mode: 'editorial',
			'json.facet': JSON.stringify({
				totalViewtime: 'sum(viewTime_int)',
				projectViews: {
					type: 'range',
					field: 'fromDate',
					start: configuration.statsDateStart.toISOString(),
					end: configuration.statsDateEnd.toISOString(),
					gap: `+1${this.granularity}`,
					facet: {
						ps: 'unique(playerSession_str)'
					}
				}
			})
		};
		if (this.projectService.projectContext) {
			params.fq.push(`context_str:"${this.projectService.projectContext}"`);
		}
		return params;
	}

	mapBucketsToGraphData(buckets: any) {
		this.xAxisLabels = [];
		this.viewers = 0;

		this.xAxisLabels = buckets.map((bucket) => {
			const v = bucket.ps || 0;
			this.viewers += v;
			return this.analyticsService.getFormattedDate(this.granularity?.toLowerCase() as AnalyticsGranularity, bucket.val, {
				fromDate: this.configuration.statsDateStart,
				toDate: this.configuration.statsDateEnd
			}, buckets);
		});
		return buckets.map((bucket) => bucket.ps || 0);
	}


	processPerformanceData(data: any, kpi: KPICalculation): StatsDataItem[] {
		const monthPoints: StatsDataItem[] = [];
		let kpiStatsData: any;
		if (data?.items?.length > 0) {
			data.items.forEach((kpiItem: any) => {
				this.projectKpiService.setPerformanceStatsData(kpiItem);
				if (kpiItem?.id && kpi && kpi.id === kpiItem.id) {
					kpiStatsData = kpiItem;
				}
			});
		}

		const multiplier = kpi?.isPercentage ? 100 : 1;

		if (kpiStatsData?.items?.length > 0) {
			const firstYear = Intl.DateTimeFormat(this.publicationService.locale, { year: 'numeric' }).format(new Date(kpiStatsData.items[0].date));
			const lastYear = Intl.DateTimeFormat(this.publicationService.locale, { year: 'numeric' }).format(new Date(kpiStatsData.items[kpiStatsData.items.length - 1].date));
			const spansMultipleYears = firstYear !== lastYear;

			kpiStatsData.items.forEach((p: any) => {
				const month = Intl.DateTimeFormat(this.publicationService.locale, KPI_DATEFORMAT_OPTIONS).format(new Date(p.date));
				monthPoints.push({
					x: month + (spansMultipleYears ? ` ${Intl.DateTimeFormat(this.publicationService.locale, { year: 'numeric' }).format(new Date(p.date))}` : ''),
					y: p.result ? p.result * multiplier : 0
				});
			});
		}

		return monthPoints;
	}

	getViewerStatsData(params: any) {
		this.loadingViewData = true;
		const loadId = 'ProjectStatsPerformanceComponent:getViewerStatsData';
		this.overlayService.increaseLoadCount(loadId);
		this.sapiService.getUrl('/stats', params).subscribe((response: any) => {
			this.overlayService.decreaseLoadCount(loadId);
			this.loadingViewData = false;
			this.viewersGraphDatasets = [];
			this.viewers = 0;
			this.haveGraphData = false;

			if (response) {
				if (response.facets) {
					if (response.facets.projectViews && response.facets.projectViews.buckets) {
						this.numPoints = response.facets.projectViews.buckets.length;
						this.totalViewtime = response.facets.totalViewtime;

						this.haveGraphData = true;
						this.viewersGraphDatasets = [];

						const viewersGraphDataset: ChartDataset = {
							label: 'Viewers',
							borderWidth: 2,
							backgroundColor: ['#3693ef'],
							borderColor: ['#3693ef'],
							data: this.mapBucketsToGraphData(response.facets.projectViews.buckets)
						};

						this.viewersGraphDatasets.push(viewersGraphDataset);
					}
				}
			}
		});

	}
}
